import type { ReactNode, Ref } from "react";
import { ArrowLeft, ArrowRight, Maximize2, Pause, PenLine, Play } from "lucide-react";

import { IconButton, MenuItem, MenuLabel, MenuSeparator, Skeleton } from "@nexus/ui";
import { cn } from "@/lib/utils";
import { PageMoreMenu } from "./PageMoreMenu";
import { PageScopeControls } from "./PageScopeControls";
import type { PagePreviewFilter } from "./usePageScopeControls";
import type { PageCommands, PageHeaderSlotRefs } from "./PresentationHeaderSlots";

export interface PageHeaderControls {
  titleVisual?: ReactNode;
  status?: ReactNode;
  /** Page-specific scope controls (an event selector…), placed among tenant, event and time. */
  filters?: ReactNode;
  /** Place `filters` after the time range instead of right after the tenant. */
  filtersAfterTime?: boolean;
  timeFirst?: boolean;
  /** Before the status and viewer controls: a way out of a mode (an editor's preview). */
  leadingAction?: ReactNode;
  /** Primary page actions (New, Edit, save state…). */
  actions?: ReactNode;
  viewActions?: ReactNode;
  /** Secondary actions (MenuItem elements) grouped in the "More actions" menu. */
  menuItems?: ReactNode;
  /**
   * The page's committing action (e.g. Publish in an editor), after the More
   * menu at the far right, where documents tools put Share or Publish.
   */
  primaryAction?: ReactNode;
  /** Leaves the page's current context (e.g. closes Event Monitor); always last. */
  closeAction?: ReactNode;
  /** Overrides which dashboard scope controls show (editor preview). */
  previewFilters?: readonly PagePreviewFilter[];
  /**
   * A slot on the description line for a context rendered elsewhere (the editor
   * preview's viewer context, as the published dashboard states it).
   */
  contextRef?: Ref<HTMLDivElement>;
  statusRef?: Ref<HTMLDivElement>;
}

/** Adds a rename button beside the title, e.g. to edit a dashboard's name and description. */
export interface PageTitleAction {
  label: string;
  onClick: () => void;
  disabled?: boolean;
}

interface PageHeaderProps extends PageHeaderControls {
  ref?: Ref<HTMLElement>;
  title: string;
  titleLoading?: boolean;
  titleAction?: PageTitleAction;
  description?: ReactNode;
  /** Same box as the page content below, so the title lines up with it. */
  widthClassName: string;
  onPresent?: () => void;
  className?: string;
  /**
   * Set while a dashboard page names itself here (`PresentationHeaderSlots`):
   * the header shows the dashboard and its page side by side, split by a
   * hairline, as the full-screen header does.
   */
  pageSlots?: PageHeaderSlotRefs;
  /** With `pageSlots`: the page commands, first in the More menu, in a group of their own. */
  pageCommands?: PageCommands | null;
  /**
   * The place for a dashboard's page controls (previous, pause, next): on the
   * right, first of the controls and set apart from the filters by a hairline.
   * Empty, it takes no room.
   */
  pageControlsSlot?: (element: HTMLElement | null) => void;
}

/** A command's key, at the right of its menu row. */
function MenuKey({ children }: { children: string }) {
  return (
    <kbd className="ml-auto pl-6 font-sans text-xs text-muted" aria-hidden>
      {children}
    </kbd>
  );
}

/**
 * The page commands of the More menu: the page's position as the group's
 * label, then previous / next and, while the pages rotate, pause / resume, with
 * their keys. The page line itself shows only the position and the next page.
 */
function PageCommandItems({ commands }: { commands: PageCommands }) {
  return (
    <>
      <MenuLabel>
        Page {commands.position} of {commands.count}
      </MenuLabel>
      <MenuItem onSelect={() => commands.move(-1)} aria-keyshortcuts="ArrowLeft">
        <ArrowLeft className="mr-2 size-4" aria-hidden />
        Previous page
        <MenuKey>←</MenuKey>
      </MenuItem>
      <MenuItem onSelect={() => commands.move(1)} aria-keyshortcuts="ArrowRight">
        <ArrowRight className="mr-2 size-4" aria-hidden />
        Next page
        <MenuKey>→</MenuKey>
      </MenuItem>
      {commands.automatic ? (
        <MenuItem onSelect={commands.togglePause} aria-keyshortcuts="Space">
          {commands.paused ? (
            <Play className="mr-2 size-4" aria-hidden />
          ) : (
            <Pause className="mr-2 size-4" aria-hidden />
          )}
          {commands.paused ? "Resume rotation" : "Pause rotation"}
          <MenuKey>Space</MenuKey>
        </MenuItem>
      ) : null}
    </>
  );
}

/**
 * The one page header: title and description on the left, the page's controls
 * on the right. Its height never depends on the page or its loading state —
 * the description line is always reserved and single-line.
 */
export function PageHeader({
  ref,
  title,
  titleLoading = false,
  titleAction,
  description,
  titleVisual,
  status,
  widthClassName,
  filters,
  filtersAfterTime = false,
  timeFirst = false,
  leadingAction,
  actions,
  viewActions,
  menuItems,
  primaryAction,
  closeAction,
  previewFilters,
  contextRef,
  statusRef,
  onPresent,
  className,
  pageSlots,
  pageCommands,
  pageControlsSlot,
}: PageHeaderProps) {
  const pageMenu = pageSlots && pageCommands ? <PageCommandItems commands={pageCommands} /> : null;
  const moreMenu =
    pageMenu || menuItems ? (
      <PageMoreMenu>
        {pageMenu}
        {pageMenu && menuItems ? <MenuSeparator /> : null}
        {menuItems}
      </PageMoreMenu>
    ) : null;
  const pageFilters = filters ? (
    <div data-header-filters className="flex min-w-0 items-center gap-2">
      {filters}
    </div>
  ) : null;

  return (
    <header
      ref={ref}
      data-page-header
      className={cn("sticky top-14 z-30 shrink-0 bg-background", className)}
    >
      <div
        className={cn(
          "@container/pageheader flex w-full flex-wrap items-start gap-x-6 gap-y-2 px-(--density-page-x) py-2",
          widthClassName,
        )}
      >
        {/*
         * With a page in the header: the dashboard (kept to about half the width, its
         * title truncating), a hairline, then the page, built alike. On a narrow header
         * the page goes under the dashboard, without the hairline.
         */}
        <div
          data-header-page-identity={pageSlots ? "" : undefined}
          className={cn(
            "contents",
            // The two identities stay together on one line: when the controls do not fit
            // beside them, the controls go under (right-aligned) rather than the page.
            // The dashboard's description truncates first, then the page's name.
            pageSlots &&
              "flex min-w-0 flex-[1_1_38rem] items-center gap-x-5 gap-y-3 @max-3xl/pageheader:flex-wrap",
          )}
        >
          <div
            data-header-identity
            className={cn(
              "group/identity flex min-w-56 flex-1 items-center gap-3.5",
              // The page follows the dashboard right after the hairline, as in full screen.
              pageSlots && "min-w-0 flex-[0_1_auto] @max-3xl/pageheader:basis-full",
            )}
          >
            {/* As tall as the title and description lines it accompanies (2.5rem + 1.25rem).
              With nothing on the description line (an editor, a page without context) it
              matches the title line alone and sits beside it, not across the empty line. */}
            {titleVisual ? (
              <div
                data-header-image
                className="flex h-15 max-w-56 shrink-0 items-center group-has-[[data-header-description]:empty]/identity:h-10 group-has-[[data-header-description]:empty]/identity:self-start"
              >
                {titleVisual}
              </div>
            ) : null}
            <div className="min-w-0 flex-1">
              <h1
                className="flex min-h-10 items-center gap-2 text-page-title font-semibold text-brand-navy dark:text-primary"
                title={titleLoading ? undefined : title}
                aria-busy={titleLoading || undefined}
              >
                {titleLoading ? (
                  <>
                    <span className="sr-only">{title}</span>
                    <Skeleton className="h-6 w-56 max-w-full" />
                  </>
                ) : (
                  <span className="min-w-0 truncate">{title}</span>
                )}
                {titleAction ? (
                  <IconButton
                    label={titleAction.label}
                    disabled={titleAction.disabled}
                    onClick={titleAction.onClick}
                    className="hover:bg-transparent hover:text-interaction-text focus-visible:text-interaction-text"
                  >
                    <PenLine className="size-4" aria-hidden />
                  </IconButton>
                ) : null}
              </h1>
              <div
                data-header-description
                // On a narrow header the parts stack, one per line, without the separator dot that
                // would otherwise start a wrapped line.
                className={cn(
                  "flex min-h-5 min-w-0 flex-wrap items-center gap-x-2 gap-y-1 text-sm leading-5 text-secondary [&>*+*]:relative [&>*+*]:pl-3 [&>*+*]:before:absolute [&>*+*]:before:top-1/2 [&>*+*]:before:left-0 [&>*+*]:before:size-[3px] [&>*+*]:before:-translate-y-1/2 [&>*+*]:before:rounded-full [&>*+*]:before:bg-current [&>*+*]:before:opacity-100 @max-md/pageheader:flex-col @max-md/pageheader:items-start @max-md/pageheader:[&>*+*]:pl-0 @max-md/pageheader:[&>*+*]:before:hidden",
                )}
              >
                {description ? (
                  <div
                    className="min-w-0 max-w-full truncate"
                    title={typeof description === "string" ? description : undefined}
                  >
                    {description}
                  </div>
                ) : null}
                {contextRef ? (
                  <div ref={contextRef} className="min-w-0 overflow-hidden empty:hidden" />
                ) : (
                  <>
                    <PageScopeControls
                      slot="tenant-context"
                      previewFilters={previewFilters}
                      description={description}
                    />
                    <PageScopeControls slot="period" previewFilters={previewFilters} />
                  </>
                )}
              </div>
            </div>
          </div>
          {pageSlots ? (
            <>
              <span
                aria-hidden
                data-presentation-separator
                className="h-15 w-px shrink-0 bg-border-strong/70 @max-3xl/pageheader:hidden"
              />
              {/* The page: its mark, its name, and its line (controls, next page). It clips at
                the hairline, so a new page seems to come out of it. */}
              <div
                data-presentation-page
                className="flex max-w-[55%] min-w-0 flex-none items-center gap-3.5 overflow-x-clip [overflow-clip-margin:0.75rem] @max-3xl/pageheader:max-w-full"
              >
                <div ref={pageSlots.mark} className="relative flex h-15 shrink-0 empty:hidden" />
                <div className="min-w-0 flex-1">
                  <div
                    ref={pageSlots.title}
                    className="relative flex min-h-10 min-w-0 items-center"
                  />
                  <div
                    ref={pageSlots.meta}
                    // Kept even when empty (a single page): the page name stays on the dashboard title's line.
                    className="flex min-h-5 min-w-0 items-center text-sm leading-5"
                  />
                </div>
              </div>
            </>
          ) : null}
        </div>
        {/*
         * Scope first (what you look at), in dependency order — tenant narrows
         * the events, the page's own filters follow, time range applies to
         * everything — then actions, then the overflow menu. Groups wrap as units.
         */}
        <div
          data-header-side
          className="ml-auto flex min-w-0 max-w-full flex-wrap items-center justify-end gap-x-3 gap-y-2"
        >
          {leadingAction}
          <div
            ref={statusRef}
            data-header-status
            className="flex min-h-10 shrink-0 items-center text-sm leading-5 text-secondary empty:hidden"
          >
            {status}
          </div>
          <div
            data-header-controls
            className="flex min-h-10 min-w-0 max-w-full flex-wrap items-center justify-end gap-x-3 gap-y-2"
          >
            {/* The page controls, then a hairline between them and the filters. */}
            <div
              ref={pageControlsSlot}
              data-header-page-controls
              className="flex shrink-0 items-center empty:hidden"
            />
            <span
              aria-hidden
              className="hidden h-6 w-px shrink-0 bg-border-strong/70 [[data-header-page-controls]:not(:empty)~&]:block"
            />
            <div
              data-header-scope
              className="flex min-w-0 flex-wrap items-center justify-end gap-2 empty:hidden"
            >
              {timeFirst ? <PageScopeControls slot="time" previewFilters={previewFilters} /> : null}
              <PageScopeControls slot="tenant" previewFilters={previewFilters} />
              {filtersAfterTime ? null : pageFilters}
              <PageScopeControls slot="event" previewFilters={previewFilters} />
              {timeFirst ? null : <PageScopeControls slot="time" previewFilters={previewFilters} />}
              {filtersAfterTime ? pageFilters : null}
            </div>
            {actions ? (
              <div data-header-actions className="flex min-w-0 items-center gap-2">
                {actions}
              </div>
            ) : null}
            {viewActions || moreMenu || onPresent ? (
              <div className="flex shrink-0 items-center gap-1">
                {viewActions}
                {onPresent ? (
                  <IconButton
                    label="Full screen"
                    variant="ghost"
                    size="lg"
                    onClick={onPresent}
                    aria-keyshortcuts="Shift+F"
                    title="Full screen (Shift+F)"
                  >
                    <Maximize2 className="size-4" aria-hidden />
                  </IconButton>
                ) : null}
                {moreMenu}
              </div>
            ) : null}
            {primaryAction}
            {closeAction}
          </div>
        </div>
      </div>
    </header>
  );
}
